import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Shimmer } from 'sfy-react'
import styles from './Chatbot.module.scss'
import { ReactComponent as User } from '../../../assets/images/user.svg'
import { ReactComponent as ChatbotLogo } from '../../../assets/images/chatbot.svg'
import ChatMessage from '../ChatMessage'
import { postRequestAsync } from '../../../common/genericAPIs'
import { setSnackbar } from '../../../redux/common'

const Chatbot = () => {
  const dispatch = useDispatch()
  const { currentLocation, messageResponse, setMessageResponse, messages, setMessages } = useOutletContext()

  const userDetails = useSelector((state) => state.user.userDetails)

  const [showShimmer, setShowShimmer] = useState(false)

  const chatBoxRef = useRef()

  useEffect(() => {
    const handleBeforeUnloadEvent = (event) => {
      event.preventDefault()
      event.returnValue = 'Changes that you made may not be saved.'
      return 'Changes that you made may not be saved.'
    }
    window.addEventListener('beforeunload', handleBeforeUnloadEvent)
    return () => {
      // setMessages([])
      window.removeEventListener('beforeunload', handleBeforeUnloadEvent)
    }
  }, [setMessages])

  const callExternalHandler = (messageResponse) => {
    setMessageResponse(messageResponse)
  }

  useEffect(() => {
    if (showShimmer) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight
    }
  }, [showShimmer])

  const getMessageResponses = useCallback(
    async (messageResponse) => {
      try {
        setShowShimmer(true)
        setMessages((messages) => [...messages, { text: messageResponse.message, isFromUser: true }])
        const response = await postRequestAsync(
          '/chat',
          {
            userID: userDetails.userID,
            emailID: userDetails.emailID,
            message: messageResponse.message,
            paymentID: messageResponse.paymentID,
            currentLocation,
          },
          'chatbot'
        )
        // console.log(response)

        setMessages((messages) => {
          const updatedMessages = messages.map((message) => {
            delete message.buttons // Hide previous action buttons
            return message
          })

          return [...updatedMessages, ...response]
        })
        // chatBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight
        setShowShimmer(false)
        setMessageResponse('')
      } catch (e) {
        console.log(e)
        dispatch(setSnackbar({ message: e?.msg || `Something Went Wrong`, type: 'error' }))
        setShowShimmer(false)
      }
    },
    [userDetails, currentLocation, setMessages, setMessageResponse, dispatch]
  )

  const initiatePayment = useCallback(
    (paymentDetails) => {
      const OPTIONS = {
        key: `rzp_test_8Eq0QZQr6E701I`,
        amount: paymentDetails.amount,
        order_id: paymentDetails.orderID,
        name: `${window.clientName[0].toUpperCase()}${window.clientName.slice(1)}`,
        prefill: {
          name: userDetails.name,
          email: userDetails.emailID,
          contact: userDetails.mobileNo,
        },
        notes: {
          app: window.header,
          id: `rzp_test_8Eq0QZQr6E701I`,
        },
        handler: (response) => {
          if (response.razorpay_payment_id) {
            // setIsError(false)
            setMessageResponse({ message: 'Payment Successful. Creating claim repair request...', paymentID: response.razorpay_payment_id })
          } else {
            // setIsError(true)
            dispatch(setSnackbar({ message: response.error || `Something Went Wrong`, type: 'error' }))
          }
        },
        theme: {
          color: window.getComputedStyle(document.querySelector(':root')).getPropertyValue('--primary'),
        },
        modal: {
          ondismiss: () => {
            // @TODO: Navigate to previous step after flow is stitched
            rzp.close()
          },
        },
      }

      const Razorpay = window.Razorpay
      let rzp = new Razorpay(OPTIONS)
      rzp.open()
    },
    [userDetails, setMessageResponse, dispatch]
  )

  useEffect(() => {
    if (messageResponse) {
      getMessageResponses(messageResponse)
    }
  }, [messageResponse, getMessageResponses])

  return messageResponse || messages?.length ? (
    <div className={styles.container}>
      <div className={styles['online-text']}>
        <span className={styles['online-circle']} />
        <span>Noah is online</span>
      </div>
      <div ref={chatBoxRef} className={styles['chat-container']}>
        {messages.map((message, index) => (
          <div key={index} className={styles['message-container']}>
            {message.isFromUser ? <User className={styles['user-image']} /> : <ChatbotLogo className={styles['message-image']} />}
            <div className={styles['message']}>
              <div className={styles['message']} dangerouslySetInnerHTML={{ __html: message.text }}></div>
              {message.buttons?.length > 0 && (
                <div className={styles['button-container']}>
                  {message.buttons.map((button) => (
                    <Button
                      key={button.displayText}
                      onClick={async () => {
                        if (button.isPaymentButton) {
                          initiatePayment(button)
                        } else {
                          setMessageResponse({ message: button.displayText })
                        }
                      }}
                      className={`button button-small button-${button.displayColourType || 'primary'} width-auto ${styles['button']}`}
                    >
                      {button.displayText}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {showShimmer && (
          <div className={styles['message-container']}>
            <ChatbotLogo className={styles['message-image']} />
            <div className='width-100'>
              <Shimmer className={{ shimmer: styles['shimmer'] }} />
              <Shimmer className={{ shimmer: `${styles['shimmer']} mt8` }} />
              <Shimmer className={{ shimmer: `${styles['shimmer']} mt8` }} />
            </div>
          </div>
        )}
      </div>
      <div className={styles['message-input-wrapper']}>
        <ChatMessage callExternalHandler={callExternalHandler} />
      </div>
    </div>
  ) : (
    <Navigate to='/home' />
  )
}

export default Chatbot
